'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { UserRole } from '@/contexts/AuthContext';
import { ImpersonatedEmployee, useImpersonation } from '@/contexts/ImpersonationContext';
import { createClient } from '@/lib/supabase/client';

interface RoleConfig {
  role: UserRole;
  label: string;
  color: string;
  bgColor: string;
}

const ROLE_CONFIGS: RoleConfig[] = [
  { role: 'Admin',   label: 'Admin',   color: 'text-indigo-700',  bgColor: 'bg-indigo-100' },
  { role: 'Manager', label: 'Manager', color: 'text-blue-700',    bgColor: 'bg-blue-100' },
  { role: 'Sales',   label: 'Sales',   color: 'text-emerald-700', bgColor: 'bg-emerald-100' },
];

// Sidebar nav items with their required roles (mirrors ROUTE_ROLE_MAP)
const NAV_PERMISSIONS: { label: string; icon: string; requiredRoles?: UserRole[] }[] = [
  { label: 'VPM Dashboard', icon: 'Squares2X2Icon', requiredRoles: ['Admin', 'Manager'] },
  { label: 'QC Dashboard', icon: 'Squares2X2Icon', requiredRoles: ['Admin', 'Manager'] },
  { label: 'Schedule', icon: 'CalendarDaysIcon' },
  { label: 'Meetings', icon: 'VideoCameraIcon' },
  { label: 'Photos', icon: 'PhotoIcon', requiredRoles: ['Admin', 'Manager'] },
  { label: 'Executive', icon: 'ChartBarSquareIcon', requiredRoles: ['Admin', 'Manager'] },
  { label: 'Financial', icon: 'BanknotesIcon', requiredRoles: ['Admin', 'Manager'] },
  { label: 'Operations', icon: 'CogIcon', requiredRoles: ['Admin', 'Manager'] },
  { label: 'Logistics', icon: 'TruckIcon', requiredRoles: ['Admin', 'Manager'] },
  { label: 'Expansion', icon: 'ArrowTrendingUpIcon', requiredRoles: ['Admin', 'Manager'] },
  { label: 'Installers', icon: 'WrenchScrewdriverIcon', requiredRoles: ['Admin', 'Manager'] },
  { label: 'Reports', icon: 'ChartBarIcon', requiredRoles: ['Admin', 'Manager'] },
  { label: 'Warranty', icon: 'ShieldCheckIcon', requiredRoles: ['Admin', 'Manager'] },
  { label: 'Escalations', icon: 'ExclamationTriangleIcon', requiredRoles: ['Admin', 'Manager'] },
  { label: 'Employees', icon: 'UsersIcon', requiredRoles: ['Admin', 'Manager'] },
  { label: 'Boards', icon: 'ViewColumnsIcon', requiredRoles: ['Admin', 'Manager', 'Sales'] },
  { label: 'Customers', icon: 'UsersIcon', requiredRoles: ['Admin', 'Manager', 'Sales'] },
  { label: 'Contacts', icon: 'IdentificationIcon', requiredRoles: ['Admin', 'Manager', 'Sales'] },
  { label: 'HR', icon: 'UserGroupIcon', requiredRoles: ['Admin', 'Manager'] },
  { label: 'My Profile', icon: 'UserCircleIcon' },
];

const getPermissionsForRoles = (roles: UserRole[]): string[] => {
  const isSA = roles.includes('Admin') || roles.includes('Manager');
  return NAV_PERMISSIONS.filter((item) => {
    if (!item.requiredRoles) return true;
    if (isSA) return true;
    return item.requiredRoles.some((r) => roles.includes(r));
  }).map((item) => item.label);
};

// Shapes returned by the API
interface ProvisionedUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  profile_image: string;
  is_disabled: boolean;
  badge: 'PROVISIONED';
}

interface UnprovisionedEmployee {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar_url: string;
  job_title: string;
  badge: 'NO LOGIN YET';
}

interface RoleUsersResult {
  role: string;
  provisioned: ProvisionedUser[];
  unprovisioned: UnprovisionedEmployee[];
}

type FetchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: RoleUsersResult }
  | { status: 'error'; message: string };

interface PendingImpersonation {
  displayName: string;
  impersonated: ImpersonatedEmployee;
  permissions: string[];
}

interface RoleSwitcherDropdownProps {
  onClose: () => void;
}

const RoleSwitcherDropdown = ({ onClose }: RoleSwitcherDropdownProps) => {
  const { startImpersonation, stopImpersonation, isImpersonating } = useImpersonation();
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [roleCounts, setRoleCounts] = useState<Record<string, number>>({});
  const [fetchState, setFetchState] = useState<FetchState>({ status: 'idle' });
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [pendingImpersonation, setPendingImpersonation] = useState<PendingImpersonation | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const supabase = createClient();

  // Load role counts on mount
  useEffect(() => {
    const fetchCounts = async () => {
      setLoadingCounts(true);
      try {
        const { data } = await supabase
          .from('user_profiles')
          .select('user_role, user_roles');

        if (data) {
          const counts: Record<string, number> = {};
          data.forEach((profile) => {
            const roles: string[] = Array.isArray(profile.user_roles) && profile.user_roles.length > 0
              ? profile.user_roles
              : [profile.user_role].filter(Boolean);
            roles.forEach((r) => {
              counts[r] = (counts[r] || 0) + 1;
            });
          });
          setRoleCounts(counts);
        }
      } catch {
        // silently fail
      } finally {
        setLoadingCounts(false);
      }
    };
    fetchCounts();
  }, []);

  // Abort in-flight fetch on unmount (Strict Mode safety)
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const fetchUsersForRole = useCallback(async (role: UserRole) => {
    // Abort any prior in-flight request
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setFetchState({ status: 'loading' });
    console.log(`[RBAC-RoleUsers] Fetching via API for role: ${role}`);

    try {
      const res = await fetch('/api/rbac/list-users-by-role', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
        signal: controller.signal,
      });

      if (controller.signal.aborted) return;

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(errBody.error ?? `HTTP ${res.status}`);
      }

      const data: RoleUsersResult = await res.json();
      console.log(`[RBAC-RoleUsers] API returned: provisioned=${data.provisioned.length}, unprovisioned=${data.unprovisioned.length}`);

      if (!controller.signal.aborted) {
        setFetchState({ status: 'success', data });
      }
    } catch (err: any) {
      if (err?.name === 'AbortError' || controller.signal.aborted) return;
      console.error(`[RBAC-RoleUsers] Fetch error for role ${role}:`, err);
      setFetchState({ status: 'error', message: err?.message ?? 'Unknown error' });
    }
  }, []);

  // Trigger fetch when selectedRole changes
  useEffect(() => {
    if (!selectedRole) {
      setFetchState({ status: 'idle' });
      return;
    }
    fetchUsersForRole(selectedRole);
  }, [selectedRole, fetchUsersForRole]);

  // Impersonate a provisioned user (has user_profiles.id)
  const handleViewProvisioned = (user: ProvisionedUser) => {
    const roles: UserRole[] = [selectedRole!];
    const impersonated: ImpersonatedEmployee = {
      id: user.id,
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      role: selectedRole!,
      roles,
      profile_image: user.profile_image || '',
    };
    const permissions = getPermissionsForRoles(roles);
    setPendingImpersonation({
      displayName: `${user.first_name} ${user.last_name}`,
      impersonated,
      permissions,
    });
  };

  // Impersonate an unprovisioned employee — effectiveUserId=null, effectiveRoles=[role]
  const handleViewUnprovisioned = (emp: UnprovisionedEmployee) => {
    const roles: UserRole[] = [selectedRole!];
    // Prefix with 'emp-' so ImpersonationContext.getEffectiveUserId returns null
    const impersonated: ImpersonatedEmployee = {
      id: `emp-${emp.id}`,
      first_name: emp.first_name,
      last_name: emp.last_name,
      email: emp.email,
      role: selectedRole!,
      roles,
      profile_image: emp.avatar_url || '',
      job_title: emp.job_title,
    };
    const permissions = getPermissionsForRoles(roles);
    setPendingImpersonation({
      displayName: `${emp.first_name} ${emp.last_name}`,
      impersonated,
      permissions,
    });
  };

  const handleConfirmImpersonation = () => {
    if (pendingImpersonation) {
      startImpersonation(pendingImpersonation.impersonated);
      setPendingImpersonation(null);
      onClose();
    }
  };

  const handleCancelImpersonation = () => {
    setPendingImpersonation(null);
  };

  const getRoleConfig = (role: UserRole) => ROLE_CONFIGS.find(r => r.role === role) || ROLE_CONFIGS[0];

  const getInitials = (first: string, last: string) =>
    `${first?.charAt(0) || ''}${last?.charAt(0) || ''}`.toUpperCase();

  const totalUsers =
    fetchState.status === 'success'
      ? fetchState.data.provisioned.length + fetchState.data.unprovisioned.length
      : 0;

  return (
    <>
      <div
        ref={dropdownRef}
        className="absolute right-0 top-full mt-2 bg-popover border border-border rounded-lg shadow-xl z-50 overflow-hidden"
        style={{ width: selectedRole ? '520px' : '280px' }}
      >
        <div className="flex">
          {/* Left panel: role list */}
          <div className={`${selectedRole ? 'w-[220px] border-r border-border' : 'w-full'} flex-shrink-0`}>
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <span className="font-heading text-sm font-semibold text-foreground">View As Role</span>
              {isImpersonating && (
                <button
                  onClick={() => { stopImpersonation(); onClose(); }}
                  className="font-caption text-xs text-error hover:text-error/80 transition-standard"
                >
                  Exit Preview
                </button>
              )}
            </div>
            <ul className="py-1 max-h-[400px] overflow-y-auto">
              {ROLE_CONFIGS.map(({ role, label, color, bgColor }) => {
                const count = roleCounts[role] || 0;
                const isSelected = selectedRole === role;
                return (
                  <li key={role}>
                    <button
                      onClick={() => setSelectedRole(isSelected ? null : role)}
                      className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left transition-standard hover:bg-muted ${isSelected ? 'bg-muted' : ''}`}
                    >
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-caption font-medium flex-1 truncate ${color} ${bgColor}`}>
                        {label}
                      </span>
                      <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-muted text-muted-foreground font-caption text-[10px] font-medium">
                        {loadingCounts ? '…' : count}
                      </span>
                      {selectedRole && (
                        <Icon
                          name="ChevronRightIcon"
                          size={14}
                          className={isSelected ? 'text-primary' : 'text-muted-foreground'}
                        />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Right panel: user list */}
          {selectedRole && (
            <div className="flex-1 flex flex-col min-w-0">
              <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                <button
                  onClick={() => setSelectedRole(null)}
                  className="text-muted-foreground hover:text-foreground transition-standard"
                  aria-label="Back to roles"
                >
                  <Icon name="ChevronLeftIcon" size={16} />
                </button>
                <span className="font-heading text-sm font-semibold text-foreground truncate">
                  {getRoleConfig(selectedRole).label} Team
                </span>
              </div>
              <div className="overflow-y-auto max-h-[400px] p-2 space-y-1.5">
                {/* Loading state */}
                {fetchState.status === 'loading' && (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 rounded-full border-2 border-[#E89A3C] border-t-transparent animate-spin" />
                  </div>
                )}

                {/* Error state */}
                {fetchState.status === 'error' && (
                  <div className="px-4 py-8 text-center">
                    <p className="text-[12px] text-red-500 mb-2">Couldn't load users.</p>
                    <button
                      onClick={() => fetchUsersForRole(selectedRole)}
                      className="text-[11px] text-primary underline hover:text-secondary transition-standard"
                    >
                      Retry
                    </button>
                  </div>
                )}

                {/* Success state */}
                {fetchState.status === 'success' && (
                  <>
                    {/* Empty state — only when BOTH arrays are empty */}
                    {totalUsers === 0 && (
                      <div className="px-4 py-8 text-center">
                        <p className="text-[12px] text-gray-500">No users assigned to this role yet.</p>
                      </div>
                    )}

                    {/* Provisioned users first */}
                    {fetchState.data.provisioned.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center gap-3 p-2.5 rounded-md border border-border hover:border-primary/30 hover:bg-muted/50 transition-standard"
                      >
                        {/* Avatar */}
                        <div className="flex-shrink-0">
                          {user.profile_image ? (
                            <img
                              src={user.profile_image}
                              alt={`${user.first_name} ${user.last_name}`}
                              className="w-9 h-9 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center">
                              <span className="font-heading text-xs font-semibold text-white">
                                {getInitials(user.first_name, user.last_name)}
                              </span>
                            </div>
                          )}
                        </div>
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1 flex-wrap">
                            <p className="font-body text-sm font-medium text-foreground truncate">
                              {user.first_name} {user.last_name}
                            </p>
                            {user.is_disabled && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-red-100 text-red-700 ml-1 flex-shrink-0">
                                DISABLED
                              </span>
                            )}
                          </div>
                          <p className="font-caption text-xs text-muted-foreground truncate">
                            {user.email}
                          </p>
                        </div>
                        {/* View Dashboard button */}
                        <button
                          type="button"
                          onClick={() => handleViewProvisioned(user)}
                          className="flex-shrink-0 px-2.5 py-1.5 bg-primary text-white rounded-md font-caption text-xs font-medium hover:bg-secondary transition-standard press-scale whitespace-nowrap"
                        >
                          View Dashboard
                        </button>
                      </div>
                    ))}

                    {/* Unprovisioned employees with yellow badge */}
                    {fetchState.data.unprovisioned.map((emp) => (
                      <div
                        key={emp.id}
                        className="flex items-center gap-3 p-2.5 rounded-md border border-border hover:border-primary/30 hover:bg-muted/50 transition-standard"
                      >
                        {/* Avatar */}
                        <div className="flex-shrink-0">
                          {emp.avatar_url ? (
                            <img
                              src={emp.avatar_url}
                              alt={`${emp.first_name} ${emp.last_name}`}
                              className="w-9 h-9 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center">
                              <span className="font-heading text-xs font-semibold text-white">
                                {getInitials(emp.first_name, emp.last_name)}
                              </span>
                            </div>
                          )}
                        </div>
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1 flex-wrap">
                            <p className="font-body text-sm font-medium text-foreground truncate">
                              {emp.first_name} {emp.last_name}
                            </p>
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-100 text-amber-700 ml-1 flex-shrink-0">
                              NO LOGIN YET
                            </span>
                          </div>
                          <p className="font-caption text-xs text-muted-foreground truncate">
                            {emp.job_title || emp.email}
                          </p>
                        </div>
                        {/* View Dashboard button — impersonates with effectiveUserId=null */}
                        <button
                          type="button"
                          title="Preview as this employee (no login — limited data access)"
                          onClick={() => handleViewUnprovisioned(emp)}
                          className="flex-shrink-0 px-2.5 py-1.5 bg-amber-500 text-white rounded-md font-caption text-xs font-medium hover:bg-amber-600 transition-standard press-scale whitespace-nowrap"
                        >
                          View Dashboard
                        </button>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      {pendingImpersonation && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleCancelImpersonation}
          />

          {/* Dialog */}
          <div className="relative bg-popover border border-border rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Icon name="EyeIcon" size={16} className="text-amber-600" />
                </div>
                <h2 className="font-heading text-base font-semibold text-foreground">Switch Role View</h2>
              </div>
              <button
                onClick={handleCancelImpersonation}
                className="text-muted-foreground hover:text-foreground transition-standard p-1 rounded-md hover:bg-muted"
                aria-label="Cancel"
              >
                <Icon name="XMarkIcon" size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-4 space-y-4">
              {/* User info */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border">
                <div className="flex-shrink-0">
                  {pendingImpersonation.impersonated.profile_image ? (
                    <img
                      src={pendingImpersonation.impersonated.profile_image}
                      alt={pendingImpersonation.displayName}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                      <span className="font-heading text-sm font-semibold text-white">
                        {getInitials(pendingImpersonation.impersonated.first_name, pendingImpersonation.impersonated.last_name)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-body text-sm font-semibold text-foreground">
                    {pendingImpersonation.displayName}
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    {pendingImpersonation.impersonated.roles.map((r) => {
                      const rc = getRoleConfig(r);
                      return (
                        <span key={r} className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-caption font-medium ${rc.color} ${rc.bgColor}`}>
                          {rc.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Permissions */}
              <div>
                <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Sidebar Access ({pendingImpersonation.permissions.length} items)
                </p>
                <div className="grid grid-cols-2 gap-1 max-h-36 overflow-y-auto">
                  {pendingImpersonation.permissions.map((perm) => (
                    <div key={perm} className="flex items-center gap-1.5 py-1 px-2 rounded-md bg-muted/40">
                      <Icon name="CheckCircleIcon" size={12} className="text-success flex-shrink-0" />
                      <span className="font-caption text-xs text-foreground truncate">{perm}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Warning */}
              <div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <Icon name="ExclamationTriangleIcon" size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="font-body text-xs text-amber-800">
                  Any unsaved work on the current page will be lost.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border bg-muted/30">
              <button
                onClick={handleCancelImpersonation}
                className="px-4 py-2 rounded-lg border border-border bg-background text-foreground font-caption text-sm font-medium hover:bg-muted transition-standard"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmImpersonation}
                className="px-4 py-2 rounded-lg bg-primary text-white font-caption text-sm font-medium hover:bg-secondary transition-standard press-scale"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RoleSwitcherDropdown;
